import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import DomainAddIcon from '@mui/icons-material/DomainAdd'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useQueryClient } from '@tanstack/react-query'
import { buildAssignmentsByRouteBase } from 'api/aws/notifications/taskNotifyHelpers'
import { useUniversalNotify } from 'api/aws/notifications/useUniversalNotify'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import { customSortUsersOperator } from 'components/DataGrid/operators/customSortOperator'
import { formatCamelCase } from 'components/DataGrid/operators/formatCamelCase'
import { isActiveThisMonth } from 'components/DataGrid/operators/isActiveThisMonth'
import { useDrawer } from 'context/TaskDrawerContext/DrawerContext'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Task } from 'types'
import { isProductsOnboarding } from 'utils/isProductsOnboarding'
import { usePostBatchTasks } from '../../api/aws/tasks/usePostBatchTasks'
import { taskTypeLabels } from '../Forms/types'
import { ALLOWED_CHECKLIST_TYPES, campaignChecklistFor, materializeChecklist } from './campaign_checklists'
import { creativeKeyFromProduct } from './CREATIVE_FIELDS'
import { buildAutoDescription } from './description_config'
import OrgRows from './OrgRows'
import { routeProductToPlatformChannel } from './productRouting'
import ProductRows from './ProductRows'
import RowDrawerWrapper from './RowDrawerWrapper'
import { denseInput } from './style'
import {
  asChannelOrEmpty,
  asPlatformOrEmpty,
  Channel,
  DescCtx,
  DIGITAL_CHANNELS,
  OrgRow,
  Platform,
  Row,
  TaskType,
  Value,
} from './taskTypes'
import { calculateDateDue, clonedOrgRow, clonedRow, defaultOrgRow, defaultRow, DRAWER_WIDTH } from './taskUtil'

/** ---------- Tiny helpers (used early in handlers) ---------- */
const toIso = (d: any) => (d ? dayjs(d).toDate().toISOString() : null)
const nowIso = () => new Date().toISOString()

const inferFromProduct = (opt: any): { platform: Platform; channel: Channel } => {
  const { platform, channel } = routeProductToPlatformChannel({
    title: opt?.title ?? opt?._raw?.productName,
    productName: opt?._raw?.productName,
    category: opt?._raw?.category ?? opt?.category_name,
    subcategory: opt?.subcategory,
    deal_uuid: opt?._raw?.deal_uuid,
  })
  return { platform, channel }
}

const buildTaskName = (
  _orgName: string,
  taskType: string,
  taskTypeLabel: string,
  _platform?: string,
  _channel?: string
) => {
  const label = taskTypeLabels?.[taskType as keyof typeof taskTypeLabels] || taskType
  return taskTypeLabel || label
}

const isOrgRowValid = (r: OrgRow) => !!(r?.org?.org_uuid && r.taskType && r.startDate)
const isProdRowValid = (r: Row) => !!(r?.org?.org_uuid && r.platform && r.channel && r.taskType && r.startDate)

const buildChecklistForType = (taskType: TaskType, row: Row) => {
  console.log('taskType', taskType)
  if (!ALLOWED_CHECKLIST_TYPES?.includes(taskType)) return { checklist: [], progress: { total: 0, done: 0 } as const }
  const keys = campaignChecklistFor({
    platform: row.platform as any,
    channel: row.channel as any,
    productTitle: row?.selectedProduct?.title,
  })
  const checklist = materializeChecklist(keys, nowIso())
  return { checklist, progress: { total: checklist.length, done: 0 } as const }
}

/** ---------- Component ---------- */
const GlobalBatchTasksSheet = ({ open: modalOpen, onClose }: { open: boolean; onClose: () => void }) => {
  const theme = useTheme()
  const qc = useQueryClient()
  const { open, openDrawer, closeDrawer, sel } = useDrawer()
  const { notifyAssignments } = useUniversalNotify()
  const xsUp = useMediaQuery(theme.breakpoints.up('xs'))
  const smUp = useMediaQuery(theme.breakpoints.up('sm'))
  const mdUp = useMediaQuery(theme.breakpoints.up('md'))
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'))
  const xlUp = useMediaQuery(theme.breakpoints.up('xl'))
  const paperRef = useRef<HTMLDivElement | null>(null)
  const resolvedDrawerWidth = useMemo(() => {
    if (typeof DRAWER_WIDTH === 'number') return DRAWER_WIDTH
    return xlUp
      ? DRAWER_WIDTH.xl
      : lgUp
      ? DRAWER_WIDTH.lg
      : mdUp
      ? DRAWER_WIDTH.md
      : smUp
      ? DRAWER_WIDTH.sm
      : DRAWER_WIDTH.xs
  }, [xlUp, lgUp, mdUp, smUp, xsUp])
  const { deals, organizations, products: products = [] } = useEntitiesFromSearch()
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { syncTasksCacheAfterBatchUpdate } = useSyncQueryCache()
  const currentUserId = localStorage.getItem('slack_user_id')
  const currentUser = useMemo(() => {
    if (!users || !currentUserId) return null
    return users?.find(u => u?.user_uuid === currentUserId) ?? null
  }, [users, currentUserId])
  const [submitting, setSubmitting] = useState(false)
  // Description editor (shared between product/org rows)
  const [descOpen, setDescOpen] = useState(false)
  const [descCtx, setDescCtx] = useState<DescCtx>(null)
  const [descValue, setDescValue] = useState<string>('')
  // Rows
  const [orgRows, setOrgRows] = useState<OrgRow[]>([])
  const [rows, setRows] = useState<Row[]>([defaultRow(currentUser ?? null)])

  /** ---------- Users list filtered/sorted for assignees ---------- */
  const filteredUsers = useMemo(() => {
    if (isUsersLoading || !users) return []
    const filtered = users?.filter(u => !['Grobotic App', 'Slackbot', 'marketing']?.includes(u?.name))
    return filtered?.length ? customSortUsersOperator(filtered) : []
  }, [users, isUsersLoading])
  const onboardingProducts = useMemo(() => {
    if (!products) return []
    return isProductsOnboarding(products)
  }, [products])

  /** ---------- Org list limited to orgs with Active/Pending products ---------- */
  const orgs = useMemo(() => {
    if (!organizations || !products) return []
    const productPKs = products
      .filter((p: any) => isActiveThisMonth(p.startDate, p.endDate) || p.status === 'Pending')
      .map((p: any) => p?.PK)
    const uniqueProductPks = new Set(productPKs)
    const uniqueOnboardingProductPks = new Set(onboardingProducts?.map(p => p?.PK))
    const allActiveOrgs = organizations?.filter(o => uniqueProductPks.has(o?.PK))
    return allActiveOrgs ?? []
    // return allActiveOrgs?.map(o => {
    //   if (
    //     o?.onboardingStatus &&
    //     o?.onboardingStatus !== 'Bypass' &&
    //     o?.onboardingStatus !== 'Launched' &&
    //     uniqueOnboardingProductPks?.has(o?.PK)
    //   )
    //     return { ...o, isOnboarding: true }
    //   return { ...o }
    // })
  }, [organizations, products])
  //@ts-ignore

  /** ---------- Product options grouped by org ---------- */
  const mapToProductOption = (p: any) => {
    const { platform, channel } = inferFromProduct({ _raw: p }) ?? {}
    return {
      title: p?.productName ?? p?.selectedProduct?.name ?? 'Product',
      subcategory: p?.subcategory ?? p?.selectedProduct?.subcategory ?? 'No category',
      category_name: p?.category ?? p?.selectedProduct?.category_name ?? 'No category',
      platform: platform,
      channel: channel,
      price: p?.price ?? 0,
      status: p?.status ?? '-',
      startDate: p?.startDate ?? null,
      endDate: p?.endDate ?? null,
      uuid: p?.products_uuid ?? p?.id,
      deal_uuid: p?.deal_uuid,
      instanceId: p?.instanceId ?? p?.id,
      id: p?.SK ?? p?.instanceId,
      SK: p?.SK ?? null,
      _raw: p,
    }
  }
  /*------------------------------------------------------------*/
  /*------------------------------------------------------------*/

  const productOptionsByOrg = useMemo(() => {
    const map = new Map<string, any[]>()
    products
      .filter((p: any) => isActiveThisMonth(p.startDate, p.endDate) || p.status === 'Pending')
      .forEach((p: any) => {
        const orgUuid = p?.PK?.startsWith('ORG_') ? p.PK.replace('ORG_', '') : p.org_uuid
        if (!orgUuid) return
        const arr = map.get(orgUuid) ?? []
        arr.push(mapToProductOption(p))
        map.set(orgUuid, arr)
      })
    return map
  }, [products])
  /** ----------  Row helpers ---------- */
  const validOrgCount = useMemo(() => orgRows.filter(isOrgRowValid).length, [orgRows])
  const validProdRowCount = useMemo(() => rows.filter(isProdRowValid).length, [rows])
  /** ---------- Org row helpers ---------- */
  const addOrgRow = () => setOrgRows(prev => [...prev, defaultOrgRow(currentUser ?? null)])
  const removeOrgRow = (idx: number) => setOrgRows(prev => prev.filter((_, i) => i !== idx))
  const updateOrgRow = (idx: number, patch: Partial<OrgRow>) =>
    setOrgRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  const cloneOrgRowFrom = (srcIdx: number) =>
    setOrgRows(prev => {
      const base = prev[srcIdx] ?? defaultOrgRow(currentUser ?? null)
      const newRow = clonedOrgRow(base, currentUser)
      const next = [...prev]
      next.splice(srcIdx + 1, 0, newRow)
      return next
    })
  /** ---------- Product row helpers ---------- */
  const addRow = () => setRows(prev => [...prev, defaultRow(currentUser ?? null)])
  const removeRow = (idx: number) => setRows(prev => prev.filter((_, i) => i !== idx))
  const updateRow = (idx: number, patch: Partial<Row>) =>
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))

  const cloneRowFrom = (srcIdx: number) =>
    setRows(prev => {
      const base = prev[srcIdx] ?? defaultRow(currentUser ?? null)
      const newRow = clonedRow(base, currentUser)
      const next = [...prev]
      next.splice(srcIdx + 1, 0, newRow)
      return next
    })
  /** ---------- Can create helpers ---------- */
  const totalRows = (rows?.length ?? 0) + (orgRows?.length ?? 0)
  const totalValid = validProdRowCount + validOrgCount
  const isReadyToCreate = useMemo(() => totalRows > 0 && totalValid === totalRows, [totalRows, totalValid])
  /** ---------- Description editor controls ---------- */
  const openDrawerFromCtx = (ctx: DescCtx, initial?: string) => {
    if (ctx?.kind && typeof ctx.idx === 'number') openDrawer({ kind: ctx.kind, idx: ctx.idx })
    setDescCtx(ctx)
    setDescValue(initial ?? '')
    setDescOpen(true) // if you still want your description editor; or skip this if the drawer replaces it
  }
  const closeDescEditor = () => {
    setDescOpen(false)
    setDescCtx(null)
  }
  const saveDescEditor = (val: string) => {
    if (!descCtx) return
    if (descCtx.kind === 'prod') updateRow(descCtx.idx, { addtlNotes: val })
    if (descCtx.kind === 'org') updateOrgRow(descCtx.idx, { addtlNotes: val })
    closeDescEditor()
  }

  /** ---------- Row change handlers ---------- */
  const resetRowDeps = (idx: number) =>
    updateRow(idx, {
      selectedProduct: null,
      platform: null,
      channel: null,
      taskType: '' as TaskType | '',
      taskTypeLabel: '',
      addtlNotes: '',
      assigner: currentUser?.user_uuid ?? '',
      assignees: [],
      checklist: [],
      progress: { total: 0, done: 0 },
      /* NEW */
      text1: '',
      text2: '',
      text3: '',
      text4: '',
    })

  const handleOrgClear = (idx: number) => {
    updateRow(idx, { org: null })
    resetRowDeps(idx)
  }

  const handleOrgChange = (idx: number, org: any) => {
    const prev = rows[idx]?.org
    const changed = (org?.org_uuid || null) !== (prev?.org_uuid || null)
    updateRow(idx, { org, isOnboarding: !!org?.isOnboarding })
    if (changed) resetRowDeps(idx)
  }

  const changeOrgTaskType = (val: Value | null, row: OrgRow, idx: number) => {
    if (!val) {
      updateOrgRow(idx, { taskType: '' as TaskType | '', taskTypeLabel: '', subType: '', addtlNotes: '' })
      if (descCtx?.kind === 'org' && descCtx.idx === idx) setDescValue('')
      return
    }

    const isUnchanged = val.base === row.taskType && (val.label || '') === (row.taskTypeLabel || '')
    if (isUnchanged) return

    const nextNotes = buildAutoDescription({
      ...row,
      taskType: val.base as TaskType,
      taskTypeLabel: val.label || '',
      subType: val?.sub || '',
    } as any)

    // Calculate dateDue if startDate exists
    const calculatedDateDue = row.startDate ? calculateDateDue(val.base, row.startDate) : null

    updateOrgRow(idx, {
      taskType: val.base as TaskType,
      taskTypeLabel: val.label || '',
      addtlNotes: nextNotes,
      dateDue: calculatedDateDue,
    })
    if (descCtx?.kind === 'org' && descCtx.idx === idx) setDescValue(nextNotes)
  }
  const changeOrgRowOrganization = (idx: number, org: any) => {
    const prev = rows[idx]?.org
    const changed = (org?.org_uuid || null) !== (prev?.org_uuid || null)
    updateOrgRow(idx, { org, isOnboarding: !!org?.isOnboarding })
    if (changed) resetRowDeps(idx)
  }
  const changeTaskType = (val: Value | null, row: Row, idx: number) => {
    if (!val) {
      updateRow(idx, {
        taskType: '' as TaskType | '',
        taskTypeLabel: '',
        subType: '',
        checklist: [],
        progress: { total: 0, done: 0 },
        addtlNotes: '',
      })
      if (descCtx?.kind === 'prod' && descCtx.idx === idx) setDescValue('')
      return
    }

    const isUnchanged = val.base === row.taskType && (val.label || '') === (row.taskTypeLabel || '')
    if (isUnchanged) return
    console.log('val.base', val.base)
    const { checklist, progress } = buildChecklistForType(val.base as TaskType, row)
    console.log('checklist', checklist, 'progress', progress)

    // Calculate dateDue if startDate exists
    const calculatedDateDue = row.startDate ? calculateDateDue(val.base, row.startDate) : null

    const nextRow: Row = {
      ...row,
      taskType: val.base as TaskType,
      taskTypeLabel: val.label || '',
      subType: val?.sub || '',
      checklist,
      progress,
      dateDue: calculatedDateDue,
    }

    const newDescription = buildAutoDescription(nextRow)
    updateRow(idx, { ...nextRow, addtlNotes: newDescription })
    if (descCtx?.kind === 'prod' && descCtx.idx === idx) setDescValue(newDescription)
  }

  /*------------------------------------------------------------*/
  const changeProduct = (newProduct: any, row: Row, idx: number) => {
    // If user picked "General Inquiry", convert this product row into an org-wide row
    if (newProduct?.__gi) {
      const org = row.org
      if (!org?.org_uuid) {
        // user picked GI without selecting org yet – keep the row, just ignore for now
        // (optional) toast: "Select an organization first."
        return
      }

      const giOrgRow: OrgRow = {
        ...defaultOrgRow(currentUser ?? null),
        org,
        taskType: 'General Inquiry' as TaskType, // simple label
        taskTypeLabel: 'General Inquiry',
        startDate: row.startDate ?? dayjs(), // carry over if already picked
        dateDue: null,
        addtlNotes: '',
        // everything else uses defaults from defaultOrgRow
      }

      setOrgRows(prev => [...prev, giOrgRow])
      removeRow(idx) // drop the product-scoped row since GI is org-scoped
      return
    }

    // ----- existing product flow (unchanged) -----
    const inferred = newProduct ? inferFromProduct(newProduct) : { platform: null, channel: null }
    const creativeKey = creativeKeyFromProduct(newProduct)

    const nextRowBase: Row = {
      ...row,
      selectedProduct: newProduct || null,
      platform: inferred.platform,
      channel: inferred.channel,
      creative: creativeKey ? { typeKey: creativeKey, values: {} } : undefined,
    }

    if (creativeKey) {
      const { checklist, progress } = buildChecklistForType(creativeKey as TaskType, nextRowBase)
      const nextRow: Row = {
        ...nextRowBase,
        taskType: creativeKey as TaskType,
        taskTypeLabel: formatCamelCase(creativeKey),
        checklist,
        progress,
      }
      const newDescription = buildAutoDescription(nextRow)
      updateRow(idx, { ...nextRow, addtlNotes: newDescription })
    } else {
      updateRow(idx, {
        ...nextRowBase,
        taskType: '' as TaskType | '',
        taskTypeLabel: '',
        checklist: [],
        progress: { total: 0, done: 0 },
        addtlNotes: '',
      })
    }

    if (descCtx?.kind === 'prod' && descCtx.idx === idx) setDescValue('')
  }

  /*------------------------------------------------------------*/

  /** ---------- Paste handler ---------- */
  const handlePasteTable = (e: React.ClipboardEvent) => {
    const text = e.clipboardData?.getData('text/plain')
    if (!text || text.indexOf('\t') === -1) return
    e.preventDefault()

    const lines = text
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(Boolean)

    const next: Row[] = lines.map(line => {
      const [orgName, platformStr, channelStr, taskType, start, due, desc] = line.split('\t')
      const org = orgs.find((o: any) => (o?.name || '').toLowerCase() === (orgName || '').toLowerCase()) || null

      return {
        ...defaultRow(currentUser ?? null),
        org,
        platform: asPlatformOrEmpty(platformStr),
        channel: asChannelOrEmpty(channelStr),
        taskType: taskType || '',
        taskTypeLabel: '',
        startDate: start ? dayjs(start) : null,
        endDate: due ? dayjs(due) : null,
        dateDue: due ? dayjs(due) : null,
        addtlNotes: desc || '',
      }
    })

    setRows(next.length ? next : [defaultRow(currentUser ?? null)])
  }

  /** ---------- Submit ---------- */
  const { mutate: createBatchTasks } = usePostBatchTasks()

  const handleSubmit = () => {
    const ready = rows.filter(isProdRowValid)
    const readyOrg = orgRows.filter(isOrgRowValid)
    if (!ready.length && !readyOrg.length) return onClose?.()
    const tasks = ready.map(r => {
      const orgId = r.org.org_uuid
      const name = buildTaskName(r.org?.name, r.taskType, r.taskTypeLabel)

      // merge any legacy Google Search extras + creative extras
      const googleExtras =
        r.platform === 'Google' && r.channel === 'Search'
          ? { text1: r.text1 ?? '', text2: r.text2 ?? '', text3: r.text3 ?? '', text4: r.text4 ?? '' }
          : undefined

      const creativeExtras = r.creative?.values && Object.keys(r.creative.values).length ? r.creative.values : undefined

      const extraFields = {
        ...(googleExtras || {}),
        ...(creativeExtras || {}),
      }
      const includeExtraFields = Object.keys(extraFields).length > 0

      return {
        PK: `ORG_${orgId}`,
        orgId,
        name,
        deal_uuid: r.selectedProduct?.deal_uuid ?? null,
        type: r.taskType,
        taskType: r.taskType,
        taskTypeLabel: r.taskTypeLabel,
        platform: r.platform,
        channel: r.channel,
        isMarketing: DIGITAL_CHANNELS?.includes(r.channel) ?? false,
        isCreative: r.channel === 'Creative' ? true : false,
        isCommandCenter: false,
        masterStatus: 'Not Started',
        website: r.org?.website || '',
        startDate: toIso(r.startDate) || nowIso(),
        endDate: toIso(r.dateDue),
        dateDue: toIso(r.dateDue),
        addtlNotes: r.addtlNotes || '',
        details: r.details || '',
        assigner: r.assigner || null,
        owner: r.assigner ?? null,
        assignees: r.assignees || [],
        queue: r.queue ?? null,
        checklist: r.checklist || [],
        progress: r.checklist?.length ? { total: r.checklist.length, done: 0 } : { total: 0, done: 0 },
        productSK: r.selectedProduct?.SK ?? null,
        productName: r.selectedProduct?.productName ?? null,
        scope: 'product',
        ...(includeExtraFields ? { ...extraFields } : {}), // only attach if non-empty
      }
    })
    console.log('tasks after format', tasks)

    const orgTasks = readyOrg.map(o => {
      const orgId = o.org.org_uuid
      const name = buildTaskName(o.org?.name, o.taskType, o.taskTypeLabel)
      return {
        PK: `ORG_${orgId}`,
        orgId,
        deal_uuid: o.selectedProduct?.deal_uuid ?? null,
        name,
        type: o.taskType,
        taskType: o.taskType,
        taskTypeLabel: o.taskTypeLabel,
        isMarketing: true,
        isCommandCenter: true,
        masterStatus: 'Not Started',
        website: o.org?.website || '',
        startDate: toIso(o.startDate) || nowIso(),
        endDate: toIso(o.endDate),
        dateDue: toIso(o.dateDue),
        addtlNotes: o.addtlNotes || '',
        details: o.details || '',
        assigner: currentUser?.user_uuid || null,
        owner: currentUser?.user_uuid || null,
        assignees: o.assignees || [],
        queue: null,
        checklist: [],
        progress: { total: 0, done: 0 },
        productSK: o?.productSK ?? null,
        scope: 'organization',
      } as const
    })

    const allTasks = [...tasks, ...orgTasks]
    console.log('allTasks', allTasks)
    setSubmitting(true)
    createBatchTasks(allTasks, {
      onSuccess: async ({ createdTasks }: { createdTasks: Task[] }) => {
        setSubmitting(false)
        syncTasksCacheAfterBatchUpdate(qc, createdTasks)

        // inside onSuccess after you have `createdTasks`
        const byBase = buildAssignmentsByRouteBase(createdTasks)

        // Notify marketing (teamtasks)
        if (Object.keys(byBase.teamtasks).length) {
          await notifyAssignments({
            assignmentsByUser: byBase.teamtasks,
            headerText: 'You were assigned to new task(s) as Assignee',
            maxLinks: 6,
            sendApp: true,
            sendSlack: true,
            routeBase: 'teamtasks', // force board context for this bucket
            role_assignment: 'Assignee',
          })
        }

        // Notify creative (projects)
        if (Object.keys(byBase.projects).length) {
          await notifyAssignments({
            assignmentsByUser: byBase.projects,
            headerText: 'You were assigned to new task(s) as Assignee',
            maxLinks: 6,
            sendApp: true,
            sendSlack: true,
            routeBase: 'projects',
            role_assignment: 'Assignee',
          })
        }

        onClose?.()
      },
      onError: () => setSubmitting(false),
    })
  }

  const drawerSpace = useMemo(() => {
    return open ? resolvedDrawerWidth : 0
  }, [open, resolvedDrawerWidth])
  useEffect(() => {
    if (rows) {
      console.log('rows', rows)
    }
  }, [])
  /** ---------- Render ---------- */
  useEffect(() => {
    if (orgRows) {
      console.log('orgRows', orgRows)
    }
  }, [])
  return (
    <Dialog
      open={modalOpen}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return
        onClose()
      }}
      fullWidth
      maxWidth="xl"
      disableEscapeKeyDown
      PaperProps={{
        ref: paperRef,
        sx: t => ({
          position: 'relative',
          maxWidth: '100dvw',
          height: '100%',
          width: '100%',
          maxHeight: '100dvh',
          margin: 0.3,
          p: 0.5,
          overflow: 'visible',
          background: 'none',
          // bgcolor: theme.palette.mode === 'dark' ? '' : 'background.toolbarlight',
          borderRadius: t.shape.borderRadius,
          // 👇 make all main sections keep clear of the drawer
          '& .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiDialogActions-root': {
            pr: `${drawerSpace}px`,
            transition: t.transitions.create('padding-right', {
              duration: 200,
              easing: t.transitions.easing.easeInOut,
            }),
          },
        }),
      }}
      onPaste={handlePasteTable}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(20px)',
            backgroundColor: 'background.toolbarlight',
          },
        },
      }}
    >
      <DialogTitle sx={{ padding: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}>
          <IconButton sx={{ justifyContent: 'flex-end' }} onClick={() => onClose()}>
            <ClearIcon />
          </IconButton>
        </Box>
        <Box>
          <Box>
            <Typography variant="h6">Global Batch Task Creation</Typography>
            {/* <Typography variant="body2" color="text.secondary">
              Platform → Channel → Task Type. Paste: Org | Platform | Channel | TaskType | Start | Due | Description
            </Typography> */}
            <Typography variant="body2" color="text.secondary">
              Product level tasks | General inquiry tasks | Copy rows | Edit rows using row selection
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
            <Chip
              label={`${totalValid} / ${totalRows} ready`}
              color={totalValid ? 'success' : 'default'}
              size="small"
            />
            <Tooltip title="Add General Inquiry / Org Level">
              <IconButton onClick={addOrgRow} size="small">
                <DomainAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Product Specific Tasks">
              <IconButton onClick={addRow} size="small">
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, overflow: 'hidden', overflowY: 'auto' }}>
        <ClickAwayListener
          mouseEvent="onMouseDown"
          onClickAway={e => {
            const t = e.target as HTMLElement

            // clicks inside the drawer stay open
            if (t.closest('.MuiDrawer-paper')) return

            // clicks inside MUI menus/popovers/autocomplete poppers stay open
            if (
              t.closest('.MuiMenu-paper') ||
              t.closest('.MuiPopover-root') ||
              t.closest('.MuiAutocomplete-popper') ||
              t.closest('[data-in-drawer]') // from step #1
            ) {
              return
            }

            // clicks in the rows area stay open (your existing rule)
            if (t.closest('[data-rows-root]')) return

            closeDrawer()
          }}
        >
          {/* one common root for both sections */}
          <Box data-rows-root>
            {/* <Box sx={{ px: 2 }}> */}
            <Box>
              {orgRows.length > 0 && (
                <OrgRows
                  orgs={orgs}
                  orgRows={orgRows}
                  onUpdateRow={updateOrgRow}
                  onRemoveRow={removeOrgRow}
                  onChangeOrg={changeOrgRowOrganization}
                  onClearOrg={handleOrgClear}
                  onChangeType={changeOrgTaskType}
                  filteredUsers={filteredUsers}
                  buildTaskName={buildTaskName}
                  openEditor={openDrawerFromCtx}
                  cloneFrom={cloneOrgRowFrom}
                />
              )}
            </Box>

            {rows.length > 0 && (
              <ProductRows
                orgs={orgs}
                rows={rows}
                onUpdateRow={updateRow}
                onRemoveRow={removeRow}
                productOptionsByOrg={productOptionsByOrg}
                onChangeOrg={handleOrgChange}
                onClearOrg={handleOrgClear}
                onChangeProduct={changeProduct}
                onChangeType={changeTaskType}
                filteredUsers={filteredUsers}
                buildTaskName={buildTaskName}
                openEditor={openDrawerFromCtx}
                closeEditor={closeDrawer}
                cloneFrom={cloneRowFrom}
              />
            )}
          </Box>
        </ClickAwayListener>
        <RowDrawerWrapper
          rows={rows}
          orgRows={orgRows}
          onUpdateProd={updateRow}
          onUpdateOrg={updateOrgRow}
          denseInput={denseInput}
          buildTaskName={buildTaskName}
          extraFields={[
            { key: 'text1', label: 'text1' },
            { key: 'text2', label: 'text2' },
            { key: 'text3', label: 'text3' },
            { key: 'text4', label: 'text4' },
          ]}
          containerEl={paperRef.current}
          drawerWidth={resolvedDrawerWidth}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={handleSubmit}
          disabled={!isReadyToCreate || submitting}
        >
          Create {totalValid || ''} Task{totalValid === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default GlobalBatchTasksSheet
